import React, { useState, useEffect, useContext } from "react";
import axiosInstance from '../services/axiosInstance';
import GlobalTable from "../components/GlobalTable";
import { useLazyTable } from "../hooks/useLazyTable";
import { format } from 'date-fns';
import { handleApiError, handleApiSuccess } from "../components/apiErrorHandler";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { AutoComplete } from "primereact/autocomplete";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import CustomerDialog from "../components/CreateCustomerDialog";
import { validatePhoneNumber } from "../components/utils";
import { useNavigate, useLocation } from "react-router-dom";
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import { UserContext, usePermission } from '../contexts/UserContext';

const TEXT_FIELDS_DEFAULTING_TO_EMPTY = [
    'title', 'gender', 'first_name', 'last_name', 'email', 'phone_number',
    'address', 'nic', 'passport', 'category', 'segment', 'loyalty',
];

function normalizeCustomerForForm(data) {
    if (!data) return data;
    const normalized = { ...data };
    for (const field of TEXT_FIELDS_DEFAULTING_TO_EMPTY) {
        if (normalized[field] === null || normalized[field] === undefined) {
            normalized[field] = '';
        }
    }
    return normalized;
}

const Customer = () => {
    const [customer, setCustomer] = useState({
        title: "",
        gender: "",
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
        address: "",
        nic: "",
        passport: "",
        dob: null,
        category: "",
        segment: "",
        loyalty: "",
        priority: 5,
        status: "Regular",
        organization: "b835b027-72ba-4c99-af9c-c8c6c1e1c732",
        relationship: "",
        relative: null,
        relative_flg: false,
        loyalty_discount_percent: 0,
    });

    const [customers, setCustomers] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const { lazyParams, onPage, onSort, onFilter, queryParams } = useLazyTable({ rows: 15 });
    const location = useLocation();
    // Set manually via Django admin per-customer (gf/models.py::OrganizationSettings.crm_only).
    // Cached in localStorage by Layout.js on load — avoids a second API call here.
    const crmOnly = (() => {
        try {
            return JSON.parse(localStorage.getItem('organizationSettings') || '{}')?.crm_only === true;
        } catch {
            return false;
        }
    })();
    const [sendingOrderReady, setSendingOrderReady] = useState({});
    const [dialogVisible, setDialogVisible] = useState(false);
    const [relativeDialog, setRelativeDialog] = useState(false);
    const [dialogName, setDialogName] = useState('Create Customer');
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [isRequired, setisRequired] = useState(true);
    const [loading, setLoading] = useState(true);
    const [selectedCustomer, setSelectedCustomer] = useState(null); 
    const [newRelation, setNewRelation] = useState({});
    const [selectedExistingCustomer, setSelectedExistingCustomer] = useState({}); // For selected customer from AutoComplete
    const [autocompleteSuggestions, setAutocompleteSuggestions] = useState([]);
    const [originalRowData, setOriginalRowData] = useState(null);
    const [relatives, setRelatives] = useState([]);
    const [relativesDialogVisible, setRelativesDialogVisible] = useState(false);
    const navigate = useNavigate();
    const { push, pop, beginDrilldown } = useBreadcrumbs();
    const canManageCustomers = usePermission('manage_customers');
    
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const customerResult = await axiosInstance.get("/api/customer/", { params: queryParams });
                setCustomers(customerResult.data.results);
                setTotalRecords(customerResult.data.count);
                setLoading(false);
            } catch (error) {
                handleApiError(error);
                setLoading(false);
            }
        };
        fetchData();

    }, [queryParams]);

    useEffect(() => {
        if (location.state?.customer) {
            setCustomer(normalizeCustomerForForm(location.state.customer));
            setOriginalRowData(location.state.customer);
            setisRequired(false);
            setDialogVisible(true); 
        }
        else if (location.state?.relative){
            setSelectedCustomer(location.state.relative);
            setRelativeDialog(true);
        }
        else if (location.state?.openCreate) {
            setDialogVisible(true); 
        }
    }, [location.state]);
        
    useEffect(() => {
        const loadCustomerFromNav = async () => {
            if (!location.state?.customerId) return;

            try {
                const res = await axiosInstance.get(
                    `/api/customer/${location.state.customerId}/`
                );
                setCustomer(normalizeCustomerForForm(res.data));
                setOriginalRowData(res.data);
                setisRequired(false);
                setDialogVisible(true);
                setDialogName('Customer Details');
                setIsReadOnly(true);
            } catch (err) {
                handleApiError(err);
            }
        };

        loadCustomerFromNav();
    }, [location.state?.customerId]);

    const handleChange = (e, field) => {
        const value = e.target ? e.target.value : e.value;
        setCustomer((prev) => {
            const updatedCustomer = { ...prev, [field]: value };
    
            // Set default title based on gender
            if (field === "gender") {
                if (value === "M") {
                    updatedCustomer.title = "Mr.";
                } else if (value === "F") {
                    updatedCustomer.title = "Mss.";
                }
            }
    
            return updatedCustomer;
        });
    };

    const handleChangeRel = (e, field) => {
        const value = e.target ? e.target.value : e.value;
    
        setSelectedExistingCustomer((prev) => ({
            ...prev,
            [field]: value, 
        }));
    };


    const handleRowClick = (rowData) => {
        setCustomer(normalizeCustomerForForm(rowData));
        setIsReadOnly(true); // Set to read-only mode
        setDialogVisible(true);
        setDialogName('Customer Details');
    };

    const relativeType = [
        { label: "Spouse", value: "Spouse"},
        { label: "Mother", value: "Mother"},
        { label: "Farther", value: "Farther"},
        { label: "Son", value: "Son"},
        { label: "Daughter", value: "Daughter"},
        { label: "Mother-in-law", value: "Mother-in-law"},
        { label: "Farther-in-law", value: "Farther-in-law"},
        { label: "Other", value: "Other"},
    ]

    const titles = [
        { label: "Mr.", value: "Mr." },
        { label: "Mss.", value: "Mss." },
        { label: "Mrs.", value: "Mrs." },
        { label: "Dr.", value: "Dr." },
        { label: "Ven.", value: "Ven." },
        { label: "Rev.", value: "Rev." },
    ];

    const segments = [
        { label: "High Revenue", value: "High Revenue" },
        { label: "Medium Revenue", value: "Medium Revenue" },
        { label: "Low Revenue", value: "Low Revenue" },
        { label: "Negative Revenue", value: "Negative Revenue" },
     ];

    const categories = [
        {label: "Store visitors", value: "Store visitors" },
        {label: "Home delivery", value: "Home delivery" },
        {label: "Convenience", value: "Convenience" },
        {label: "Loyal", value: "Loyal" },
    ];

    const priority = [
        {label: 1, value: 1 },
        {label: 2, value: 2 },
        {label: 3, value: 3 },
        {label: 4, value: 4 },
        {label: 5, value: 5 },
    ];

    const status = [
        {label: "Regular", value: "Regular" },
        {label: "Blacklisted", value: "Blacklisted" },
        {label: "Inactive", value: "Inactive" },
    ];

    const onRowEditInit = (e) => {
        setOriginalRowData(e.data); // Save the original row data
    };

    const handleRowEditComplete = async (e) => {
        let newData;
        if (e?.newData) {
            newData = e.newData;
        } 
        else if (e) {
            newData = e;
        } 
        if (originalRowData.phone_number && !newData.phone_number) {
            handleApiError({ Error: "Phone number cannot be cleared, you can only change." });
            return false;
        }   
        
        const validatedPhoneNumber = validatePhoneNumber(newData.phone_number ? newData.phone_number : '0700000000');
        if (!validatedPhoneNumber) {
            handleApiError({ Error: "Invalid phone number. Please enter a valid number." });
            return false;
        }
        const formattedCustomer = {
            ...newData,
            phone_number: newData.phone_number ? validatedPhoneNumber : null, // Use validated phone number
            dob: newData.dob ? format(new Date(newData.dob), 'yyyy-MM-dd') : null, // Ensure proper format
        };

        if (
            (newData.gender === "M" && newData.title !== "Mr." && newData.title !== "Dr." && newData.title !== "Ven." && newData.title !== "Rev.") ||
            (newData.gender === "F" && newData.title !== "Mss." && newData.title !== "Mrs." && newData.title !== "Dr." && newData.title !== "Ven." && newData.title !== "Rev.")
        ) {
            handleApiError({ Error: "Invalid title for the selected gender" });
            return false;
        }

        try {
            const updatedCustomer = await axiosInstance.put(
                `/api/customer/${formattedCustomer.id}/`,
                formattedCustomer
            );
            setCustomers((prevCustomers) => {
                const filteredCustomers = prevCustomers.filter(item => item.id !== updatedCustomer.data.id);
                return [updatedCustomer.data, ...filteredCustomers];
            });
            setDialogVisible(false);
            handleApiSuccess('Customer updated successfully!');
            return true;
        } catch (error) {
            handleApiError(error);
            return false;
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const validatedPhoneNumber = validatePhoneNumber(customer.phone_number ? customer.phone_number : '0700000000');
        if (!validatedPhoneNumber) {
            handleApiError({ Error: "Invalid phone number. Please enter a valid number." });
            return;
        }
        const formattedCustomer = {
            ...customer,
            phone_number: customer.phone_number ? validatedPhoneNumber : null, // Use validated phone number
            dob: customer.dob ? format(new Date(customer.dob), 'yyyy-MM-dd') : null, // Ensure proper format
            relationship: newRelation.relationship ? newRelation.relationship : "",
            relative: newRelation.relative ? newRelation.relative : null,
            relative_flg: newRelation.relative ? true : false,
        };
    
        // Ensure title matches gender selection
        if (
            (customer.gender === "M" && customer.title !== "Mr." && customer.title !== "Dr." && customer.title !== "Ven." && customer.title !== "Rev.") ||
            (customer.gender === "F" && customer.title !== "Mss." && customer.title !== "Mrs." && customer.title !== "Dr." && customer.title !== "Ven." && customer.title !== "Rev.")
        ) {
            handleApiError({ Error: "Invalid title for the selected gender" });
            return;
        }
        try {
            let result;
            if (!customer.id) {
            result = await axiosInstance.post("/api/customer/", formattedCustomer);
            const updatedCustomer = result.data;
            setCustomers([updatedCustomer, ...customers]);

            if (newRelation.relative){
            const selresult = await axiosInstance.patch(`/api/customer/${newRelation.relative}/`, { relative_flg: true });
            setCustomers((prevCustomers) => {
                const filteredCustomers = prevCustomers.filter(item => item.id !== selresult.data.id);
                return [selresult.data, ...filteredCustomers];
            });
            }
        } else {
            const editSucceeded = await handleRowEditComplete(customer);
            if (!editSucceeded) {
                return;
            }
        }
            setCustomer({
                title: "",
                gender: "",
                first_name: "",
                last_name: "",
                email: "",
                phone_number: "",
                address: "",
                nic: "",
                passport: "",
                dob: null,
                category: "",
                segment: "",
                loyalty: "",
                priority: 5,
                status: "Regular",
                organization: "b835b027-72ba-4c99-af9c-c8c6c1e1c732",
                relationship: "",
                relative: null,
                relative_flg: false,
                loyalty_discount_percent: 0,
            });
            setNewRelation({});
            setSelectedCustomer(null);
            setSelectedExistingCustomer({});
            // handleRowEditComplete already shows its own "Customer updated
            // successfully!" toast and closes the dialog on the edit path —
            // only build this create-specific message when `result` exists.
            if (result) {
                handleApiSuccess(`Customer ${result.data.first_name} ${result.data.last_name} saved successfully.`);
            }
            setDialogVisible(false);
            setisRequired(true);
        } catch (error) {
            handleApiError(error);
        }
    };

    const saveRelative = async () => {
        if (!selectedExistingCustomer.relationship) {
            handleApiError({Error:'Please select a Relationship.'});
            return;
        }
    
        if (!selectedExistingCustomer.customerObject || !selectedExistingCustomer.customerObject.id) {
            handleApiError({Error:'Please select a valid existing customer from the dropdown.'});
            return;
        }

        try {

            const parUpdatedCustomer = await axiosInstance.patch(
                `/api/customer/${selectedExistingCustomer.customerObject.id}/`,
                {
                    relationship: selectedExistingCustomer.relationship,
                    relative: selectedCustomer.id,
                    relative_flg: true,
                }
            );
            setCustomers((prevCustomers) => {
                const filteredCustomers = prevCustomers.filter(item => item.id !== parUpdatedCustomer.data.id);
                return [parUpdatedCustomer.data, ...filteredCustomers];
            });
            
            const parselectedCustomer = await axiosInstance.patch(
                `/api/customer/${selectedCustomer.id}/`,
                {
                    relative_flg: true,
                }
            );
            setCustomers((prevCustomers) => {
                const filteredCustomers = prevCustomers.filter(item => item.id !== parselectedCustomer.data.id);
                return [parselectedCustomer.data, ...filteredCustomers];
            });
            
            setNewRelation({});
            setSelectedCustomer(null);
            setSelectedExistingCustomer({});
            setRelativeDialog(false);
            handleApiSuccess('Relationship updated successfully!');
        }
        catch (error) {
            handleApiError(error);
        }
    }
    
    const handleSendOrderReady = async (rowData) => {
        if (sendingOrderReady[rowData.id]) return;
        setSendingOrderReady(prev => ({ ...prev, [rowData.id]: true }));
        try {
            await axiosInstance.post(`/api/customers/${rowData.id}/order-ready-sms/`);
            handleApiSuccess(`Order ready SMS sent to ${rowData.first_name || ''} ${rowData.last_name}.`.trim());
        } catch (error) {
            handleApiError(error);
        } finally {
            setSendingOrderReady(prev => ({ ...prev, [rowData.id]: false }));
        }
    };

    const handleRelativeClick = async (rowData) => {
        try {
            if (rowData.relative_id || rowData.id) {
                const response = await axiosInstance.post("/api/customer/relatives/", {
                    row_id: rowData.id,
                    relative_id: rowData.relative,
                });
    
                const relativesData = response.data;
    
                // Combine and display data in a dialog or another component
                setRelatives(relativesData); // Assuming `setRelatives` is a state handler
                setRelativesDialogVisible(true); // Open the dialog
            } else {
                handleApiError({ Error: "No relatives found for this customer." });
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    const fetchAutocompleteSuggestions = (query) => {
            axiosInstance
                    .get(`/api/customerq?query=${query}`)
                    .then((res) => {
                        const processedSuggestions = res.data.map((cust) => ({
                            ...cust,
                            displayLabel: `${cust.first_name} ${cust.last_name}`,
                        }));
                        setAutocompleteSuggestions(processedSuggestions);
                    })
                    .catch((error) => handleApiError(error));
    }

    const columns = [
        { Header: "Title", accessor: "title", sortable: true },
        { Header: "First Name", accessor: "first_name", sortable: true },
        {
            Header: "Last Name",
            accessor: "last_name",
            sortable: true,
            bodyTemplate: (rowData) => (
            <span
                style={{ color: '#2563eb', cursor: 'pointer' }}
                onClick={() =>{
                    beginDrilldown();
                    navigate(`/customers/${rowData.id}`);
                    push({
                        label: `Customers`,
                        path: '/customers',
                    });
                    push({
                        label: `Customer: ${rowData.last_name}`,
                        path: `/customers/${rowData.id}`,
                    });
                }}
            >
                {rowData.last_name}
            </span>
            )
        },
        { Header: "Email", accessor: "email", sortable: true },
        { Header: "Phone Number", accessor: "phone_number", sortable: true, filtered:true },
        { Header: "Gender", accessor: "gender", sortable: true },
        { Header: "Discount %", accessor: "loyalty_discount_percent", filtered:true, dataType: "numeric" },
        { Header: "Address", accessor: "address" },
        { Header: "NIC #", accessor: "nic", sortable: true },
        { Header: "Passport #", accessor: "passport", sortable: true },
        { Header: "Date of Birth", accessor: "dob", sortable: true },
        { Header: "Category", accessor: "category", sortable: true },
        { Header: "Segment", accessor: "segment", sortable: true },
        { Header: "Priority", accessor: "priority", sortable: true },
        { Header: "Status", accessor: "status", sortable: true },
        { 
            Header: "Relatives", 
            accessor: "relative_flg",
            bodyTemplate: (rowData) => {
                if (rowData.relative_flg === true) {
                    return (
                        <i
                            className="pi pi-users"
                            style={{ fontSize: "1.2rem", color: "blue", display: 'flex', justifyContent: 'center', cursor: "pointer" }}
                            title="Has Relatives"
                            onClick={() => handleRelativeClick(rowData)}
                        ></i>
                    );
                }
                return null; // Return nothing for "No" or blank values
            }, 
            dataType: "icon"
        },
        ...(crmOnly ? [{
            Header: "Order Ready SMS",
            accessor: "order_ready_sms",
            bodyTemplate: (rowData) => {
                const isLoading = !!sendingOrderReady[rowData.id];
                return (
                    <Button
                        icon="pi pi-send"
                        label="Order Ready"
                        className="p-button-sm p-button-outlined"
                        disabled={!rowData.phone_number || isLoading}
                        loading={isLoading}
                        onClick={() => handleSendOrderReady(rowData)}
                        tooltip={
                            rowData.phone_number
                                ? 'Send an "order ready for pickup" SMS to this customer'
                                : 'Customer has no phone number on record'
                        }
                        tooltipOptions={{ position: 'top' }}
                    />
                );
            },
        }] : []),
    ];

    const handleAddClick = () => {
        setCustomer({
            title: "",
            gender: "",
            first_name: "",
            last_name: "",
            email: "",
            phone_number: "",
            address: "",
            nic: "",
            passport: "",
            dob: null,
            category: "",
            segment: "",
            loyalty: "",
            priority: 5,
            status: "Regular",
            organization: "b835b027-72ba-4c99-af9c-c8c6c1e1c732",
            relationship: "",
            relative: null,
            relative_flg: false,
            loyalty_discount_percent: 0,
        });
        setIsReadOnly(false);
        setDialogName('Create Customer');
        setDialogVisible(true);
        setisRequired(true)
    };

    const headerAction = [
        ...(canManageCustomers ? [
            <Button key="add-customer" type="button" icon="pi pi-plus" label=" Add Customer" outlined onClick={handleAddClick} className="w-full sm:w-auto" />,
            <Button key="add-relative" label="Add Relative" icon="pi pi-users" outlined onClick={() => setRelativeDialog(true)} disabled={!selectedCustomer} />,
        ] : []),
    ];

    return (
        <div>
            <div className="containers">
                <GlobalTable
                    columns={columns}
                    headerAction={headerAction}
                    data={customers}
                    tabletitle="Customer List"
                    loading={loading}
                    lazy
                    totalRecords={totalRecords}
                    lazyParams={lazyParams}
                    onPage={onPage}
                    onSort={onSort}
                    onFilter={onFilter}
                    onRowEditComplete={handleRowEditComplete}
                    onRowEditInit={onRowEditInit}
                    showEditColumn={canManageCustomers}
                    selectable="single"
                    selection={selectedCustomer}
                    onSelectionChange={(e) => setSelectedCustomer(e.value)}
                    customEditors={{
                        priority: (options) => (
                            <Dropdown
                                value={options.rowData.priority}
                                options={priority}
                                onChange={(e) => options.editorCallback(options.rowData.priority = e.value)}
                            />
                        ),
                        title: (options) => (
                            <Dropdown
                                value={options.rowData.title}
                                options={titles}
                                onChange={(e) => options.editorCallback(options.rowData.title = e.value)}
                            />
                        ),
                        gender: (options) => (
                            <Dropdown
                                value={options.rowData.gender}
                                options={['M','F']}
                                onChange={(e) => options.editorCallback(options.rowData.gender = e.value)}
                            />
                        ),
                        dob: (options) => (
                            <Calendar
                                value={options.rowData.dob ? new Date(options.rowData.dob) : null}
                                onChange={(e) => options.editorCallback(options.rowData.dob = e.value)}
                            />
                        ),
                        category: (options) => (
                            <Dropdown
                                value={options.rowData.category}
                                options={categories}
                                onChange={(e) => options.editorCallback(options.rowData.category = e.value)}
                            />
                        ),
                        segment: (options) => (
                            <Dropdown
                                value={options.rowData.segment}
                                options={segments}
                                onChange={(e) => options.editorCallback(options.rowData.segment = e.value)}
                            />
                        ),
                        status: (options) => (
                            <Dropdown
                                value={options.rowData.status}
                                options={status}
                                onChange={(e) => options.editorCallback(options.rowData.status = e.value)}
                            />
                        ),
                    }}
                />
            <CustomerDialog
                visible={dialogVisible}
                onHide={() => {setDialogVisible(false); pop();}}
                customer={customer}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                isReadOnly={isReadOnly}
                dialogName={dialogName}
                isRequired={isRequired}
                titles={titles}
                categories={categories}
                segments={segments}
                priority={priority}
                status={status}
            />
        </div>
            <Dialog 
                header="Add Relative" 
                visible={relativeDialog} 
                style={{ width: '40rem', borderRadius: '12px', padding: '.5rem' }}
                onHide={() => {setRelativeDialog(false); pop();}}
            >
                <div style={{ borderRadius: '12px', padding: '1rem' }}>
                <div className="field" style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                    <label htmlFor="relationship" className="font-bold">Relationship:</label>
                    <Dropdown
                        id="relationship"
                        value={selectedExistingCustomer.relationship}
                        options={relativeType}
                        onChange={(e) => handleChangeRel(e, "relationship")}
                        placeholder="Select Relation"
                        style={{borderRadius:"6px", minWidth:"200px"}}
                        showClear
                    />
                </div><br /><br />
                <div className="formgrid grid">
                    <div className="field col">
                    <label htmlFor="existingCustomer" className="font-bold">Existing Customer:</label>
                    <AutoComplete
                        id="existingCustomer"
                        value={selectedExistingCustomer.customerObject}
                        suggestions={autocompleteSuggestions}
                        completeMethod={(e) => fetchAutocompleteSuggestions(e.query)} // Implement this to fetch data from API
                        field="displayLabel" // Adjust this field based on your backend response
                        onChange={(e) => handleChangeRel(e, "customerObject")}
                        placeholder="Type in Phone Number/Name"
                    />
                    </div>
                    <div className="field col">
                        <label className="font-bold" style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>Or</label>
                    </div>
                    <div className="field col">
                    <Button
                        label="Add New Customer"
                        icon="pi pi-plus"
                        onClick={() => {
                            if (!selectedExistingCustomer.relationship) {
                                handleApiError({Error:'Please select a Relationship.'});
                                return;
                            }
                            else {
                                setNewRelation({relationship: selectedExistingCustomer.relationship, relative: selectedCustomer.id,});
                            }
                            setRelativeDialog(false); 
                            setDialogVisible(true); 
                            setisRequired(false);
                        }}
                        style={{borderRadius:"6px"}}
                    />
                    </div>
                    </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '1rem' }}>
                    <Button
                        label="Save Existing"
                        icon="pi pi-save"
                        disabled={!selectedExistingCustomer}
                        onClick={() => saveRelative()}
                        style={{borderRadius:"6px"}}
                    />
                    <Button
                        label="Clear"
                        icon="pi pi-eraser"
                        onClick={() => setSelectedExistingCustomer([])}
                        style={{borderRadius:"6px"}}
                        outlined
                    />
                </div>
            </Dialog>
            <Dialog
                header="Relatives"
                visible={relativesDialogVisible}
                style={{ width: '50vw' }}
                onHide={() => setRelativesDialogVisible(false)}
                dismissableMask
            >
                <DataTable value={relatives}>
                    <Column field="first_name" header="First Name" />
                    <Column 
                        field="last_name" 
                        header="Last Name" 
                        body={(rowData) => (
                            <a
                                href={`/customers/${rowData.id}`} 
                                style={{ textDecoration: 'none', color: 'blue', cursor: 'pointer' }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate(`/customers/${rowData.id}`); 
                                }}
                            >
                                {rowData.last_name}
                            </a>
                        )} 
                    />
                    <Column field="phone_number" header="Phone Number" />
                    <Column field="relationship" header="Relationship" />
                </DataTable>
            </Dialog>


        </div>
    );
};

export default Customer;